/**
 * 商品圖片區：單圖或多圖輪播（主圖 + image_urls）
 * 用於 OnlineShop / OnlineOrder / Preorder 的 Drawer 與列表縮圖
 * 多圖時支援：左右箭頭、圓點點擊、手指左右滑動（跟手 + 放開吸附動畫）
 */

import { useRef, useState, useEffect, useLayoutEffect } from 'react';

/** 放開時吸附動畫時長（ms） */
const SNAP_DURATION_MS = 250;
/** 跟手時允許的最大過拖距離（佔容器寬度比例），避免拖過頭 */
const MAX_OVERDRAG_RATIO = 0.4;

export type ProductImageGalleryProps = {
  /** 主圖 URL（img_url） */
  primaryUrl: string | null | undefined;
  /** 多張圖片 URL（image_urls）；主圖可不在其中，會自動合併為 [primaryUrl, ...urls] */
  imageUrls?: string[];
  /** 正規化後的 URL（用於顯示），若未傳則使用 primaryUrl/imageUrls 原樣 */
  normalizedUrls?: string[];
  alt: string;
  /** 列表縮圖時為 true（單圖、不輪播） */
  thumbnail?: boolean;
  className?: string;
  imageClassName?: string;
};

/**
 * 合併主圖與多圖為單一陣列（主圖優先，再去重）
 */
function getAllImages(
  primaryUrl: string | null | undefined,
  imageUrls?: string[]
): string[] {
  const primary = primaryUrl ? [primaryUrl] : [];
  const rest = imageUrls ?? [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of [...primary, ...rest]) {
    if (u && !seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  }
  return out;
}

/**
 * 單圖或多圖輪播（多圖時：滑動條 + 跟手 swipe + 放開吸附，與箭頭／圓點一致）
 */
export function ProductImageGallery({
  primaryUrl,
  imageUrls,
  normalizedUrls,
  alt,
  thumbnail = false,
  className = '',
  imageClassName = '',
}: ProductImageGalleryProps) {
  const raw = getAllImages(primaryUrl, imageUrls);
  const merged =
    normalizedUrls && normalizedUrls.length > 0 ? normalizedUrls : raw;
  const urls = merged.filter((u, i) => !i || merged.indexOf(u) === i);
  const [index, setIndex] = useState(0);
  const hasMultiple = !thumbnail && urls.length > 1;

  const containerRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  /** 跟手時：起始 X、起始 translate（px） */
  const dragRef = useRef<{
    startX: number;
    startTranslate: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!containerRef.current || !hasMultiple) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      if (el.offsetWidth) setContainerWidth(el.offsetWidth);
    });
    ro.observe(el);
    if (el.offsetWidth) setContainerWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, [hasMultiple]);

  /** index 變動時同步 strip 位置（含箭頭／圓點點擊後的動畫） */
  useEffect(() => {
    if (!stripRef.current || !containerWidth || urls.length === 0) return;
    const tx = -index * containerWidth;
    stripRef.current.style.transition = `transform ${SNAP_DURATION_MS}ms ease-out`;
    stripRef.current.style.transform = `translate3d(${tx}px,0,0)`;
  }, [index, containerWidth, urls.length]);

  const goPrev = () => setIndex(i => (i <= 0 ? urls.length - 1 : i - 1));
  const goNext = () => setIndex(i => (i >= urls.length - 1 ? 0 : i + 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!hasMultiple || !containerWidth || !stripRef.current) return;
    const t = e.touches[0];
    if (!t) return;
    const currentTx = -index * containerWidth;
    dragRef.current = { startX: t.clientX, startTranslate: currentTx };
    stripRef.current.style.transition = 'none';
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (
      !hasMultiple ||
      !dragRef.current ||
      !stripRef.current ||
      !containerWidth
    )
      return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - dragRef.current.startX;
    const maxDrag = containerWidth * MAX_OVERDRAG_RATIO;
    const clampedDx = Math.max(-maxDrag, Math.min(maxDrag, dx));
    const tx = dragRef.current.startTranslate + clampedDx;
    stripRef.current.style.transform = `translate3d(${tx}px,0,0)`;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (
      !hasMultiple ||
      !dragRef.current ||
      !stripRef.current ||
      !containerWidth
    ) {
      dragRef.current = null;
      return;
    }
    const t = e.changedTouches[0];
    if (!t) {
      dragRef.current = null;
      return;
    }
    const dx = t.clientX - dragRef.current.startX;
    const currentTx = dragRef.current.startTranslate + dx;
    dragRef.current = null;

    const threshold = containerWidth * 0.2;
    let nextIndex = index;
    if (dx > threshold) nextIndex = index <= 0 ? urls.length - 1 : index - 1;
    else if (dx < -threshold)
      nextIndex = index >= urls.length - 1 ? 0 : index + 1;
    nextIndex = Math.max(0, Math.min(urls.length - 1, nextIndex));

    setIndex(nextIndex);
    stripRef.current.style.transition = `transform ${SNAP_DURATION_MS}ms ease-out`;
    stripRef.current.style.transform = `translate3d(${-nextIndex * containerWidth}px,0,0)`;
  };

  if (urls.length === 0) {
    return (
      <div
        className={`w-full aspect-video rounded-lg bg-gray-100 flex items-center justify-center ${className}`}
      >
        <svg
          className="w-12 h-12 text-gray-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    );
  }

  if (!hasMultiple) {
    return (
      <div className={`relative w-full aspect-video ${className}`}>
        <img
          src={urls[0]}
          alt={alt}
          className={`w-full h-full object-cover rounded-lg bg-gray-100 ${imageClassName}`}
          draggable={false}
        />
      </div>
    );
  }

  /** 尚未量到寬度時先顯示第一張，避免閃爍 */
  if (containerWidth <= 0) {
    return (
      <div
        ref={containerRef}
        className={`relative w-full aspect-video ${className}`}
      >
        <img
          src={urls[0]}
          alt={alt}
          className={`w-full h-full object-cover rounded-lg bg-gray-100 ${imageClassName}`}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative w-full aspect-video overflow-hidden ${className}`}
      style={{ touchAction: 'pan-y' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <div
        ref={stripRef}
        className="flex h-full will-change-transform"
        style={{
          width: containerWidth * urls.length,
          transform: `translate3d(${-index * containerWidth}px,0,0)`,
        }}
      >
        {urls.map((url, i) => (
          <div
            key={i}
            className="flex-shrink-0 h-full flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden"
            style={{ width: containerWidth }}
          >
            <img
              src={url}
              alt={i === 0 ? alt : `${alt} ${i + 1}`}
              className={`w-full h-full object-cover ${imageClassName}`}
              draggable={false}
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        aria-label="上一張"
        onClick={e => {
          e.stopPropagation();
          goPrev();
        }}
        className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center text-sm"
      >
        ‹
      </button>
      <button
        type="button"
        aria-label="下一張"
        onClick={e => {
          e.stopPropagation();
          goNext();
        }}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center text-sm"
      >
        ›
      </button>
      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-0.5 items-center">
        {urls.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`第 ${i + 1} 張`}
            onClick={e => {
              e.stopPropagation();
              setIndex(i);
            }}
            className={`!min-w-0 !min-h-0 p-0 w-2 h-2 rounded-full transition-colors shrink-0 ${
              i === index
                ? 'bg-white ring-[0.5px] ring-gray-400'
                : 'bg-white/60'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
